import React, { useEffect, useRef, useState } from 'react';
import ActionButton from '~components/atoms/ActionButton';

import Modal from '~components/atoms/Modal';
import Text from '~components/atoms/Text';
import combinedActions from '~redux/combinedActions';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import * as Styles from './ExportDailyBalancingModal.module.scss';
import LabelledDatepicker from '~components/atoms/LabelledDatepicker';
import { useFormik } from 'formik';
import Yup from '~yup';
import dayjs from 'dayjs';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { GoldTypeEnum, SilverTypeEnum } from '~utils/enums';
import { TDropdownOption } from '~types/components/labelledDropdown';
import DailyBalancingPDF from './DailyBalancingPDF';
import { pdf } from '@react-pdf/renderer';

const ExportDailyBalancingModal = () => {
    const dispatch = useAppDispatch();

    const isOpen = useAppSelector(state => state.dashboard.modals.isExportDailyBalancingModalOpen);
    const setIsOpen = (open: boolean) => dispatch(combinedActions.dashboardSetExportDailyBalancingModalIsOpen(open));

    const inventoryList = useAppSelector((state) => state.gold.allProducts);
    const dailyBalancingData = useAppSelector(state => state.dashboard.dashboardExportDailyBalancingReport);
    const isLoading = useAppSelector(state => state.dashboard.loadings.exportDashboardDailyBalancingReport);
    const isError = useAppSelector(state => state.dashboard.errors.exportDashboardDailyBalancingReport);

    const exportError = useAppSelector(state => state.dashboard.errors.exportDashboardDailyBalancingReport);

    const exportPendingRef = useRef(false);
    const requestStartedRef = useRef(false);

    const [productName, setProductName] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    const exportDailyBalancingFormValidation = Yup.object().shape({
        date: Yup.string()
            .matchNoLeadingAndTrailingWhiteSpaces()
            .matchMaxOneWhiteSpace()
            .label('Date')
            .required('Date is required')
            .test(
                'valid-export-date',
                'Selected date is not available until the market closes',
                function (value) {
                    if (!value) return true;

                    const selectedDate = dayjs(value);
                    if (!selectedDate.isValid()) return false;

                    return !isDateDisabled(selectedDate.toDate());
                }
            ),
        productName: Yup.string().matchNoLeadingAndTrailingWhiteSpaces().matchMaxOneWhiteSpace().required('Product Name is required'),
    });

    const ExportDailyBalancingInitialValues = {
        date: '',
        productId: 0,
        productName: '',
    }

    useEffect(() => {
        if (!isOpen) {
            formik.resetForm();
            setProductName('');
            dispatch(combinedActions.dashboardResetExportDailyBalancingReportState());
        }
    }, [isOpen]);

    useEffect(() => {
        if (!exportPendingRef.current) return;

        if (isLoading) {
            requestStartedRef.current = true;
            return;
        }

        if (!requestStartedRef.current) return;

        if (exportError) {
            exportPendingRef.current = false;
            requestStartedRef.current = false;
            setIsExporting(false);
            return;
        }

        if (!dailyBalancingData) {
            exportPendingRef.current = false;
            requestStartedRef.current = false;
            setIsExporting(false);
            return;
        }

        exportPendingRef.current = false;
        requestStartedRef.current = false;

        handleExportPDF(formik.values);
    }, [isLoading, exportError, dailyBalancingData]);

    const formik = useFormik({
        initialValues: ExportDailyBalancingInitialValues,
        validationSchema: exportDailyBalancingFormValidation,
        validateOnMount: true,
        onSubmit: async values => {
            exportPendingRef.current = true;
            requestStartedRef.current = false;

            setIsExporting(true);

            dispatch(combinedActions.dashboardExportDailyBalancingReportAttempt({
                date: dayjs(values.date).format('YYYY-MM-DD'),
                productId: values.productId,
            }));
        },
    });

    const handleOnModalClose = () => {
        exportPendingRef.current = false;
        requestStartedRef.current = false;

        formik.resetForm();
        setProductName('');
        setIsExporting(false);
        setIsOpen(false);
    };

    const inventoryOptions = () => {
        return inventoryList
            .filter(item =>
                item.goldType === GoldTypeEnum.PaperGold ||
                item.silverType === SilverTypeEnum.PaperSilver
            )
            .map(item => ({
                label: item.productName,
                value: item.productId,
            }))
            .sort((a, b) => Number(a.value) - Number(b.value));
    };

    const handleOnSelectProduct = (option: TDropdownOption) => {
        const product = inventoryList.find(item => String(item.productId) === String(option.value));

        if (!product) return;

        setProductName(product.productName);

        formik.setValues({
            ...formik.values,
            productName: product.productName,
            productId: product.productId,
        }, true);
    };

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setProductName(value);

        const product = inventoryList.find(item => item.productName === value &&
            (
                item.goldType === GoldTypeEnum.PaperGold ||
                item.silverType === SilverTypeEnum.PaperSilver
            )
        );

        formik.setValues({
            ...formik.values,
            productName: value,
            productId: product ? product.productId : 0,
        }, true);
    };

    const handleClear = () => {
        formik.resetForm();
        setProductName('');
    };

    const handleExportPDF = (values: { date: string; productId: number; productName: string; }) => {
        setIsExporting(true);

        const doc = (
            <DailyBalancingPDF
                data={dailyBalancingData}
            />
        );

        return pdf(doc).toBlob()
            .then(blob => {
                const pdfUrl = URL.createObjectURL(blob);

                // const link = document.createElement('a');
                // link.href = pdfUrl;
                // link.download = `Daily_Balancing_${dayjs(values.date).format('YYYY-MM-DD')}.pdf`;

                // document.body.appendChild(link);
                // link.click();
                // link.remove();

                // setTimeout(() => URL.revokeObjectURL(pdfUrl), 1000);

                // handleOnModalClose();
                const newWindow = window.open(pdfUrl, '_blank');

                if (!newWindow) {
                    URL.revokeObjectURL(pdfUrl);
                    return;
                }

                handleOnModalClose();
            })
            .catch(error => {
                console.error('Failed to export PDF:', error);
            })
            .finally(() => {
                setIsExporting(false);
            });
    };

    const MARKET_CLOSE_HOUR = 16;
    const MARKET_CLOSE_MINUTE = 30;

    const isDateDisabled = (date: Date): boolean => {
        const now = dayjs();
        const selectedDate = dayjs(date);

        // Disable future dates
        if (selectedDate.isAfter(now, 'day')) {
            return true;
        }

        // Disable today until market closes
        if (selectedDate.isSame(now, 'day')) {
            const marketCloseTime = now
                .hour(MARKET_CLOSE_HOUR)
                .minute(MARKET_CLOSE_MINUTE)
                .second(0)
                .millisecond(0);

            return now.isBefore(marketCloseTime);
        }

        return false;
    };

    const [, setCurrentTime] = useState(Date.now());

    useEffect(() => {
        if (!isOpen) return;

        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 30_000);

        return () => clearInterval(interval);
    }, [isOpen]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleOnModalClose}
            title='Export Account-I Daily Balancing PDF'
        >
            <form onSubmit={formik.handleSubmit} className={Styles.container}>
                <div className={Styles['form-subcontainer']}>
                    <div className={Styles['field-label']}>Date</div>
                    <div className={Styles['field-colon']}>:</div>
                    <div className={Styles['field-input']}>
                        <LabelledDatepicker
                            id='date'
                            name='date'
                            label=''
                            selectedDate={formik.values.date}
                            error={formik.submitCount > 0 ? formik.errors.date : undefined}
                            setSelectedDate={(date: string) =>formik.setFieldValue('date', dayjs(date).toISOString())}
                            filterDate={(date: Date) => !isDateDisabled(date)}
                            onBlur={formik.handleBlur}
                        />
                    </div>

                    <div className={Styles['field-label']}>Product Name</div>
                    <div className={Styles['field-colon']}>:</div>
                    <div className={Styles['field-input']}>
                        <LabelledDropdown
                            id='productName'
                            name='productName'
                            label=''
                            options={inventoryOptions()}
                            value={productName}
                            error={formik.submitCount > 0 && !formik.values.productName ? formik.errors.productName : undefined}
                            onChange={handleOnSearchProduct}
                            onSelectOption={handleOnSelectProduct}
                            noOptionsMessage='No product found'
                            autoComplete='off'
                            optionSelected={inventoryOptions().some(
                                option => Number(option.value) === formik.values.productId && option.label === formik.values.productName
                            )}
                        />
                    </div>
                </div>

                <div className={Styles['button-container']}>
                    <ActionButton
                        label='Clear'
                        type='button'
                        className={Styles['clear-button']}
                        onClick={handleClear}
                        disabled={!formik.dirty && !productName}
                    />
                    <ActionButton
                        label='Export PDF'
                        type='submit'
                        isLoading={isExporting}
                    />
                </div>
                <Text
                    isError
                    label={isError}
                />

                <Text className={Styles['note-text']} label='NOTE:' />
                <Text className={Styles['note-text']} label='1) Export for current day can only be performed after market has closed.' />
                <Text className={Styles['note-text']} label='2) Transactions performed after the market has closed will only be include in the following day report.' />
            </form>
        </Modal>
    );
};

export default ExportDailyBalancingModal;
